import {isValidationFailedResponse, ValidationFailures} from '@/api';
import {Stub} from '@/api/models';
import {FeedFile} from '@/api/models/FeedFiles.ts';
import {Feed, Feeds} from '@/api/models/Feeds.ts';
import ConfirmNavigationDialog from '@/components/ConfirmNavigationDialog.tsx';
import FormControls from '@/components/FormControls.tsx';
import Pending from '@/components/Pending.tsx';
import FeedFileSelect from '@/components/Selects/FeedFileSelect.tsx';
import Title from '@/components/Title.tsx';
import ValidatedFormGroup, {extract} from '@/components/ValidatedFormGroup.tsx';
import Mapper, {MemoryFeedField} from '@/pages/feeds/Editor/Mapper';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, Callout, InputGroup} from '@blueprintjs/core';
import {
	ChangeEventHandler,
	EventHandler,
	ReactElement,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {Navigate, useNavigate, useParams} from 'react-router';

export default function FeedEditorPage(): ReactElement {
	const {id = 'new'} = useParams<'id'>();

	const feed = useMemo(() => {
		if (id === 'new')
			return Promise.resolve(null);
		else
			return Feeds.read(parseInt(id));
	}, [id]);

	return (
		<>
			<Title title={(id === 'new' ? 'Create' : 'Edit') + ' Feed'} />

			<Pending resource={feed}>
				{item => <Editor feed={item} />}
			</Pending>
		</>
	);
}

enum FieldsFailureKind {
	Empty = 'empty',
	MissingVin = 'missing vin',
}

const FIELDS_FAILURE_VALUES: { [key in FieldsFailureKind]: string } = {
	[FieldsFailureKind.Empty]: 'You must add at least one field.',
	[FieldsFailureKind.MissingVin]: 'You must include an entry that maps to the VIN schema field.',
};

interface EditorProps {
	feed: Feed | null,
}

function Editor({feed}: EditorProps): ReactElement {
	const [saving, setSaving] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [redirect, setRedirect] = useState('');

	const [validationFailures, setValidationFailures] = useState<ValidationFailures | null>(null);
	const [fieldsFailure, setFieldsFailure] = useState<FieldsFailureKind | null>(null);

	const [name, setName] = useState('');

	// Copy name from API response
	useEffect(() => {
		setName(feed?.name ?? '');
	}, [feed?.name]);

	const onNameChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setName(event.currentTarget.value);
		setDirty(true);
	}, []);

	const [file, setFile] = useState<Stub<FeedFile, 'name' | 'path'> | null>(null);

	// Copy file from API response
	useEffect(() => {
		setFile(feed?.file ?? null);
	}, [feed?.file]);

	const onFileChange = useCallback((file: FeedFile) => {

		setDirty(true);
		setFile(file);
	}, []);

	const [fields, setFields] = useState<MemoryFeedField[]>([]);

	// Copy fields from API response
	useEffect(() => {
		setFields(feed?.fields ?? []);
	}, [feed?.fields]);

	const onFieldsChange = useCallback((fields: MemoryFeedField[]) => {
		setDirty(true);
		setFields(fields);
	}, []);

	const onSave: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		if (saving)
			return;
		else if (!file) {
			Toaster.warning('You must select a source file.');
			return;
		}

		setSaving(true);

		let promise: Promise<Feed>;

		if (feed?.id) {
			promise = Feeds.update(feed.id, {
				name,
				fields,
				file: file.id,
			});
		} else {
			promise = Feeds.create({
				name,
				fields,
				file: file.id,
			});
		}

		try {
			await promise;
		} catch (error) {
			if (isValidationFailedResponse(error)) {
				Toaster.error(error.error.message);
				setValidationFailures(error.error.context.failures);

				const failures = error.error.context.failures;

				if (extract(failures, 'fields', true))
					setFieldsFailure(FieldsFailureKind.Empty);
				else if (extract(failures, 'vinField'))
					setFieldsFailure(FieldsFailureKind.MissingVin);
				else
					setFieldsFailure(null);
			} else
				ToastErrors.fromError(error);

			return;
		} finally {
			setSaving(false);
		}

		setDirty(false);
		setRedirect('/feeds');
	}, [feed?.id, fields, name, file, saving]);

	const navigate = useNavigate();

	const onCancelClick = useCallback(() => {
		navigate(-1);
	}, []);

	if (redirect.length > 0)
		return <Navigate to={redirect} />;

	return (
		<form onSubmit={onSave}>
			<div style={{display: 'flex', gap: 10}}>
				<ValidatedFormGroup label="Name" labelFor="name" fill={true} failures={validationFailures}>
					<InputGroup id="name" value={name} onChange={onNameChange} />
				</ValidatedFormGroup>

				<ValidatedFormGroup label="Source File" labelFor="sourceFile" fill={true} failures={validationFailures}>
					<FeedFileSelect current={file} onChange={onFileChange} />
				</ValidatedFormGroup>
			</div>

			{fieldsFailure && (
				<Callout intent="warning" style={{marginBottom: 10}}>
					{FIELDS_FAILURE_VALUES[fieldsFailure]}
				</Callout>
			)}

			<Mapper file={file} fields={fields} onFieldsChange={onFieldsChange} />

			<FormControls>
				<Button text="Cancel" disabled={saving} onClick={onCancelClick} />
				<Button type="submit" intent="primary" text="Save" onClick={onSave} loading={saving} />
			</FormControls>

			<ConfirmNavigationDialog dirty={dirty} />
		</form>
	);
}
